import { useMemo, useState } from 'react'
import { MdOutlinePictureAsPdf, MdOutlineEditNote } from 'react-icons/md'
import { useSession } from '@wiggot/auth/hooks/useSession'
import {
  generateUrlPublicDatasheet,
  composeClasses,
  copyToClipboard
} from '@wiggot/utils'
import ItemShare from './ItemShare'
import { camelToSnake } from '../../../../utils'
import { ROUTES } from '../../../../constants'
import type { IPropertyDetail } from '../../../../../interfaces/src'

export const ShareWithContactLead = ({
  property,
  setShowModal,
  type = 'lead'
}: {
  property: IPropertyDetail
  setShowModal(showModal: boolean): void
  type?: 'lead' | 'broker'
}) => {
  const [isCopied, setIsCopied] = useState(false)

  const { profile } = useSession()

  const linkToShare =
    type === 'lead'
      ? `${generateUrlPublicDatasheet({
          property: {
            ...property,
            propertyId: property?.propertyId
          } as any
        })}?profileId=${profile?.profileId}`
      : `${window.location.origin}/my-properties/property-detail/${property.propertyId}`

  const copyUrl = () => {
    copyToClipboard(linkToShare)
    setIsCopied(true)

    setTimeout(() => {
      setIsCopied(false)
    }, 1500)
  }

  const sendToWhatsApp = () => {
    const whatsappLink = `https://api.whatsapp.com/send?text=${encodeURIComponent(
      linkToShare
    )}`
    window.open(whatsappLink, '_blank')
  }

  const sendToMessenger = () => {
    const messengerLink = `https://www.facebook.com/dialog/send?app_id=m.me&link=${encodeURIComponent(
      linkToShare
    )}&redirect_uri=${encodeURIComponent(linkToShare)}`
    window.open(messengerLink, '_blank')
  }

  const params = useMemo(() => {
    const camelParams = camelToSnake(property)
    const params = new URLSearchParams({
      ...camelParams,
      associations: JSON.stringify(camelParams.associations),
      reports: JSON.stringify(camelParams.reports),
      property_title: property.title ?? '',
      property_id: property.propertyId,
      isWiggot: 'true'
    })
    return params.toString()
  }, [property])

  const onEdit = async () => {
    window.open(`${ROUTES.PDF_EDIT}?${params}`, '_blank')
  }

  const cleanUrl = () => {
    const newString = 'new.'
    if (linkToShare.includes(newString)) {
      const cleanedUrl = linkToShare.replace(newString, '')
      return cleanedUrl
    }
    return linkToShare
  }

  return (
    <div className="w-full flex flex-col gap-6">
      <div className="w-full flex gap-6 justify-center flex-col md:flex-row">
        <div className="flex gap-6 justify-center">
          <ItemShare
            label="WhatsApp"
            onClick={sendToWhatsApp}
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              >
                <path
                  d="M8.99426 0C4.07821 0 0.0766423 4.00104 0.0745568 8.91866C0.0740355 10.4906 0.48488 12.025 1.26538 13.3775L0 18L4.72836 16.7596C6.03128 17.4703 7.49791 17.8446 8.99061 17.8451H8.99426C13.9098 17.8451 17.9114 13.8436 17.9135 8.92596C17.9145 6.54275 16.9875 4.30188 15.3034 2.61627C13.6194 0.930136 11.3801 0.00104275 8.99426 0ZM8.99426 16.3389H8.99114C7.66111 16.3389 6.3561 15.9807 5.21794 15.3055L4.94734 15.1449L2.14181 15.8811L2.89051 13.1455L2.71429 12.865C1.97237 11.6846 1.58029 10.3201 1.58081 8.91971C1.58238 4.83212 4.90824 1.50678 8.99739 1.50678C10.9776 1.5073 12.8389 2.27946 14.2383 3.68092C15.6382 5.08186 16.4082 6.94473 16.4077 8.92544C16.4062 13.013 13.0803 16.3389 8.99426 16.3389ZM13.0605 10.7868C12.8379 10.6752 11.7419 10.1361 11.5375 10.0615C11.3332 9.98697 11.1846 9.94995 11.036 10.1731C10.8874 10.3962 10.4604 10.8983 10.3306 11.0469C10.2007 11.196 10.0704 11.2143 9.84776 11.1027C9.62513 10.9911 8.90667 10.7555 8.05579 9.99635C7.39312 9.40563 6.94578 8.6757 6.81595 8.45255C6.68613 8.22941 6.80188 8.10897 6.91397 7.99791C7.01408 7.89833 7.1366 7.73775 7.24818 7.6074C7.35975 7.47706 7.39677 7.38425 7.4708 7.23514C7.54484 7.08655 7.50782 6.9562 7.45203 6.84463C7.39625 6.73306 6.95047 5.63608 6.76486 5.18978C6.58394 4.75547 6.40042 4.81387 6.26329 4.80709C6.13347 4.80031 5.98488 4.79927 5.83629 4.79927C5.6877 4.79927 5.4463 4.85506 5.24192 5.07821C5.03754 5.30136 4.46194 5.84046 4.46194 6.93743C4.46194 8.03441 5.26069 9.09437 5.37174 9.24296C5.48332 9.39155 6.94317 11.6429 9.17883 12.6079C9.71064 12.8373 10.1257 12.9745 10.4494 13.0772C10.9833 13.2466 11.4692 13.2226 11.853 13.1653C12.281 13.1011 13.1715 12.6262 13.3571 12.1053C13.5428 11.585 13.5428 11.1387 13.487 11.0454C13.4312 10.9526 13.2826 10.8968 13.06 10.7852L13.0605 10.7868Z"
                  fill="#222222"
                />
              </svg>
            }
          />
          <ItemShare
            label="Messenger"
            onClick={sendToMessenger}
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M11.625 2.85938C6.55504 2.85938 2.625 6.57314 2.625 11.5894C2.625 14.2132 3.70032 16.4805 5.45151 18.0465C5.59852 18.1781 5.68726 18.3624 5.69329 18.5597L5.7423 20.1606C5.75796 20.6713 6.28543 21.0036 6.75271 20.7973L8.53909 20.0087C8.69052 19.9419 8.86021 19.9295 9.01978 19.9734C9.84067 20.1992 10.7144 20.3194 11.625 20.3194C16.695 20.3194 20.625 16.6056 20.625 11.5894C20.625 6.57314 16.695 2.85938 11.625 2.85938Z"
                  fill="#222222"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M6.22182 14.1432L8.86558 9.94885C9.28611 9.28159 10.1867 9.11545 10.8176 9.58867L12.9204 11.1657C13.1133 11.3104 13.3787 11.3097 13.5708 11.1639L16.4107 9.00862C16.7897 8.72098 17.2845 9.17458 17.0308 9.57711L14.3871 13.7715C13.9665 14.4387 13.0659 14.6049 12.435 14.1317L10.3322 12.5546C10.1393 12.4099 9.87385 12.4107 9.68175 12.5565L6.84193 14.7117C6.4629 14.9993 5.96811 14.5457 6.22182 14.1432Z"
                  fill="white"
                />
              </svg>
            }
          />
        </div>
        <div className="flex gap-6 justify-center">
          <ItemShare
            label="PDF"
            onClick={() => setShowModal(true)}
            icon={
              <MdOutlinePictureAsPdf className="text-[24px] text-primary" />
            }
          />
          <ItemShare
            label="Editar PDF"
            onClick={onEdit}
            icon={<MdOutlineEditNote className="text-[24px] text-primary" />}
          />
        </div>
      </div>
      <div className="w-full max-w-full flex flex-col sm:flex-row gap-4">
        <input
          className="flex items-center px-4 bg-white rounded-lg h-[52px] w-full sm:w-[calc(100%-134px)] truncate overflow-hidden !outline-none"
          value={cleanUrl()}
          name="linkToShare"
          readOnly
          type="text"
        />
        <button
          type="button"
          className={composeClasses(
            isCopied
              ? 'bg-notif-green-01 border-notif-green'
              : 'bg-notif-turquoise-01 border-notif-turquoise hover:bg-notif-turquoise hover:bg-opacity-60 active:bg-notif-turquoise ',
            'border-[1px] !min-w-fit px-4 rounded-full text-sm font-normal h-[52px]'
          )}
          onClick={copyUrl}
        >
          {isCopied ? 'Enlace copiado' : 'Copiar enlace'}
        </button>
      </div>
    </div>
  )
}

export default ShareWithContactLead
